import React, { useEffect, useState } from "react";
import { api } from "@/planner/lib/apiClient";
import { useAuth } from "@/planner/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Sparkles, Trash2, ShoppingBasket, DollarSign, Boxes, ListChecks, Download } from "lucide-react";

export default function ShoppingLists() {
  const { canEdit } = useAuth();
  const [lists, setLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [picked, setPicked] = useState({});
  const [active, setActive] = useState(null);
  const [buy, setBuy] = useState(null); // shopping item being logged as an on-sale purchase
  const [buyForm, setBuyForm] = useState({ cases: 1, paid: "" });

  // Quick "list from machines" flow.
  const [qOpen, setQOpen] = useState(false);
  const [machines, setMachines] = useState([]);
  const [qSel, setQSel] = useState({}); // machineId -> bool
  const [qName, setQName] = useState("");
  const [qNameTouched, setQNameTouched] = useState(false);
  const [qItems, setQItems] = useState([]); // filtered suggestions preview
  const [qLoading, setQLoading] = useState(false);

  const selectedMachineIds = Object.keys(qSel).filter((k) => qSel[k]);

  const autoName = (ids) => {
    if (ids.length === 0) return "";
    if (ids.length === 1) {
      const m = machines.find((x) => x.id === ids[0]);
      return `Restock: ${m?.name || "machine"}`;
    }
    return `Restock: ${ids.length} machines`;
  };

  const openQuick = async () => {
    setQSel({}); setQName(""); setQNameTouched(false); setQItems([]);
    setQOpen(true);
    try { const { data } = await api.get("/machines"); setMachines(data); }
    catch (err) { console.error("Failed to load machines:", err); setMachines([]); }
  };

  const toggleMachine = (id, checked) => {
    const next = { ...qSel, [id]: checked };
    setQSel(next);
    const ids = Object.keys(next).filter((k) => next[k]);
    if (!qNameTouched) setQName(autoName(ids));
  };

  // Refresh the previewed items whenever the machine selection changes.
  useEffect(() => {
    if (!qOpen) return;
    const ids = selectedMachineIds;
    if (ids.length === 0) { setQItems([]); return; }
    let cancelled = false;
    setQLoading(true);
    api.get(`/inventory/suggestions?machine_ids=${ids.join(",")}`)
      .then(({ data }) => { if (!cancelled) setQItems(data); })
      .catch((err) => { console.error("Failed to load suggestions:", err); if (!cancelled) setQItems([]); })
      .finally(() => { if (!cancelled) setQLoading(false); });
    return () => { cancelled = true; };
  }, [qOpen, JSON.stringify(selectedMachineIds)]); // eslint-disable-line react-hooks/exhaustive-deps

  const qUnits = qItems.reduce((s, it) => s + (it.qty || 0), 0);
  const qCost = qItems.reduce((s, it) => s + ((it.cases_needed != null && it.case_price != null) ? it.cases_needed * it.case_price : 0), 0);

  const createFromMachines = async () => {
    if (selectedMachineIds.length === 0) return toast.error("Pick at least one machine");
    if (qItems.length === 0) return toast.error("Those machines are fully stocked — nothing to buy");
    const finalName = (qName || autoName(selectedMachineIds) || "Restock list").trim();
    const items = qItems.map((s) => ({
      product_id: s.product_id, name: s.name, qty: s.qty, size: s.size || "",
      case_pack: s.case_pack ?? null, cases: s.cases_needed ?? null, case_price: s.case_price ?? null, checked: false,
    }));
    try { await api.post("/shopping-lists", { name: finalName, items }); toast.success("List created"); setQOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };


  const openBuy = (item) => {
    setBuy(item);
    setBuyForm({ cases: item.cases || 1, paid: item.case_price != null ? String(item.case_price) : "" });
  };
  const buySavings = () => {
    const cs = Number(buyForm.cases) || 0;
    const paid = Number(buyForm.paid);
    if (!buy || buy.case_price == null || isNaN(paid)) return 0;
    return (buy.case_price - paid) * cs;
  };
  const submitBuy = async () => {
    const cs = Number(buyForm.cases) || 0;
    const paid = Number(buyForm.paid);
    if (cs <= 0) return toast.error("Enter number of cases");
    if (isNaN(paid) || paid < 0) return toast.error("Enter the price you paid per case");
    try {
      const { data } = await api.post("/purchases", {
        product_id: buy.product_id, product_name: buy.name, size: buy.size || "",
        cases: cs, case_pack: buy.case_pack || 0,
        baseline_case_price: buy.case_price, paid_case_price: paid,
      });
      toast.success(`Logged ${cs} case${cs !== 1 ? "s" : ""} — $${data.savings.toFixed(2)} extra profit`);
      setBuy(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to log purchase"); }
  };

  const load = async () => { const { data } = await api.get("/shopping-lists"); setLists(data); };
  useEffect(() => { load(); }, []);

  const openCreate = async () => {
    setName(""); setPicked({});
    try { const { data } = await api.get("/inventory/suggestions"); setSuggestions(data); } catch (err) { console.error("Failed to load restock suggestions:", err); setSuggestions([]); }
    setOpen(true);
  };

  const create = async () => {
    if (!name) return toast.error("Name required");
    const items = suggestions.filter((s) => picked[s.product_id]).map((s) => ({
      product_id: s.product_id, name: s.name, qty: s.qty, size: s.size || "",
      case_pack: s.case_pack ?? null, cases: s.cases_needed ?? null, case_price: s.case_price ?? null, checked: false,
    }));
    try { await api.post("/shopping-lists", { name, items }); toast.success("List created"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const listTotal = (l) => l.items.reduce((sum, it) => sum + ((it.cases != null && it.case_price != null) ? it.cases * it.case_price : 0), 0);

  const toggleItem = async (list, idx) => {
    const items = list.items.map((it, i) => i === idx ? { ...it, checked: !it.checked } : it);
    const { data } = await api.put(`/shopping-lists/${list.id}`, { items });
    setLists(lists.map((l) => l.id === list.id ? data : l));
    if (active?.id === list.id) setActive(data);
  };

  // A CSV for Vistar's order upload: Vistar item number, barcode, description and cases still to buy.
  const vistarCsv = async (l) => {
    try {
      const res = await api.get(`/shopping-lists/${l.id}/vistar.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `vistar-${(l.name || "list").replace(/[^a-z0-9]+/gi, "-")}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Couldn't build the Vistar CSV"); }
  };
  const del = async (id) => { if (!window.confirm("Delete list?")) return; await api.delete(`/shopping-lists/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div><h1 className="font-heading text-2xl font-bold">Shopping Lists</h1><p className="text-sm text-muted-foreground">Build lists from machine needs, check off in the store.</p></div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="shopping-quick-machines-button" onClick={openQuick}><Boxes className="mr-2 h-4 w-4" />List by machine</Button>
            <Button data-testid="shopping-add-button" onClick={openCreate}><Plus className="mr-2 h-4 w-4" />New list</Button>
          </div>
        )}
      </div>

      {lists.length === 0 ? <Card className="p-10 text-center text-muted-foreground">No shopping lists yet.</Card> : (
        <div className="grid gap-4 lg:grid-cols-2">
          {lists.map((l) => {
            const done = l.items.filter((i) => i.checked).length;
            return (
              <Card key={l.id} className="p-4" data-testid={`shopping-list-${l.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-heading font-semibold flex items-center gap-2"><ShoppingBasket className="h-4 w-4 text-primary" />{l.name}</div>
                  <div className="flex items-center gap-2"><Badge variant="secondary">{done}/{l.items.length}</Badge><Button variant="outline" size="sm" data-testid={`shopping-vistar-${l.id}`} onClick={() => vistarCsv(l)}><Download className="mr-1.5 h-4 w-4" />Vistar CSV</Button>{canEdit && <Button variant="ghost" size="icon" data-testid={`shopping-delete-${l.id}`} onClick={() => del(l.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>}</div>
                </div>
                <div className="mt-3 space-y-2">
                  {l.items.length === 0 ? <p className="text-sm text-muted-foreground">Empty list.</p> : l.items.map((it, idx) => (
                    <label key={it.product_id ? it.product_id : `manual-${idx}`} className="flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer" data-testid={`shopping-item-${l.id}-${idx}`}>
                      <Checkbox checked={it.checked} onCheckedChange={() => toggleItem(l, idx)} data-testid={`shopping-check-${l.id}-${idx}`} />
                      <div className={`flex-1 min-w-0 ${it.checked ? "line-through text-muted-foreground" : ""}`}>
                        <div className="text-sm font-medium truncate">{it.name}{it.size ? <span className="ml-1 text-xs text-muted-foreground">· {it.size}</span> : null}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {it.cases != null ? `${it.cases} case${it.cases !== 1 ? "s" : ""}${it.case_pack ? ` × ${it.case_pack}` : ""} = ${it.qty} units` : `${it.qty} units`}
                        </div>
                      </div>
                      <div className="text-right">
                        {it.cases != null ? <span className="font-mono text-sm font-semibold" data-testid={`shopping-cases-${l.id}-${idx}`}>{it.cases} cs</span> : <span className="font-mono text-sm">x{it.qty}</span>}
                        {it.cases != null && it.case_price != null && <div className="text-[11px] text-muted-foreground">${(it.cases * it.case_price).toFixed(2)}</div>}
                        {canEdit && it.case_price != null && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-1 h-6 px-1.5 text-[10px] font-medium text-primary hover:bg-primary/10"
                            data-testid={`shopping-buy-${l.id}-${idx}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openBuy(it); }}
                          >
                            <DollarSign className="mr-0.5 h-3 w-3" />Bought on sale
                          </Button>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                {listTotal(l) > 0 && (
                  <div className="mt-3 flex items-center justify-between border-t pt-2 text-sm" data-testid={`shopping-total-${l.id}`}>
                    <span className="text-muted-foreground">Estimated cost</span>
                    <span className="font-mono font-bold">${listTotal(l).toFixed(2)}</span>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="shopping-dialog">
          <DialogHeader><DialogTitle>New shopping list</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>List name</Label><Input data-testid="shopping-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Monday route" /></div>
            <div>
              <div className="flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-primary" />Suggested from low slots</div>
              {suggestions.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No low slots right now — create an empty list and add items.</p> : (
                <div className="mt-2 space-y-2 max-h-[40vh] overflow-y-auto">
                  {suggestions.map((s) => (
                    <label key={s.product_id} className="flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer" data-testid={`suggest-${s.product_id}`}>
                      <Checkbox checked={!!picked[s.product_id]} onCheckedChange={(v) => setPicked({ ...picked, [s.product_id]: v })} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">{s.name}{s.size ? <span className="text-xs text-muted-foreground"> · {s.size}</span> : null} <span className="text-xs text-muted-foreground">({s.machines.length} machine{s.machines.length > 1 ? "s" : ""})</span></div>
                        <div className="text-[11px] text-muted-foreground">{s.cases_needed != null ? `${s.cases_needed} case${s.cases_needed !== 1 ? "s" : ""}${s.case_pack ? ` × ${s.case_pack}` : ""} = ${s.qty} units` : `${s.qty} units · set case pack on product`}</div>
                      </div>
                      <div className="text-right">
                        {s.cases_needed != null ? <span className="font-mono text-sm font-semibold">{s.cases_needed} cs</span> : <span className="font-mono text-sm">x{s.qty}</span>}
                        {s.case_cost != null && <div className="text-[11px] text-muted-foreground">${s.case_cost.toFixed(2)}</div>}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button data-testid="shopping-create-button" onClick={create}>Create list</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={qOpen} onOpenChange={setQOpen}>
        <DialogContent data-testid="shopping-quick-dialog">
          <DialogHeader><DialogTitle>Quick list by machine</DialogTitle>
            <DialogDescription>Pick one or more machines — we'll total up everything below par into a ready-to-shop list.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium"><Boxes className="h-4 w-4 text-primary" />Machines</div>
            {machines.length === 0 ? <p className="text-sm text-muted-foreground">No machines available.</p> : (
              <div className="space-y-2 max-h-[34vh] overflow-y-auto">
                {machines.map((m) => (
                  <label key={m.id} className="flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer hover:bg-muted/50" data-testid={`quick-machine-${m.id}`}>
                    <Checkbox checked={!!qSel[m.id]} onCheckedChange={(v) => toggleMachine(m.id, !!v)} data-testid={`quick-machine-check-${m.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{m.name}</div>
                      {(m.location || m.model_name) && <div className="text-[11px] text-muted-foreground truncate">{[m.model_name, m.location].filter(Boolean).join(" · ")}</div>}
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div><Label>List name</Label><Input data-testid="quick-name-input" value={qName} onChange={(e) => { setQName(e.target.value); setQNameTouched(true); }} placeholder="Restock: Monday route" /></div>

            <div className="rounded-lg border bg-muted/30 p-3" data-testid="quick-preview">
              {selectedMachineIds.length === 0 ? (
                <p className="text-sm text-muted-foreground">Select machines to preview what needs restocking.</p>
              ) : qLoading ? (
                <p className="text-sm text-muted-foreground">Calculating needs…</p>
              ) : qItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">Those machines are fully stocked — nothing to buy right now.</p>
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  <ListChecks className="h-4 w-4 text-primary" />
                  <span className="font-medium" data-testid="quick-preview-summary">{qItems.length} product{qItems.length !== 1 ? "s" : ""} · {qUnits} units{qCost > 0 ? ` · ~$${qCost.toFixed(2)}` : ""}</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQOpen(false)} data-testid="quick-cancel">Cancel</Button>
            <Button onClick={createFromMachines} disabled={qItems.length === 0} data-testid="quick-create-button"><Plus className="mr-1 h-4 w-4" />Create list</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!buy} onOpenChange={(o) => !o && setBuy(null)}>
        <DialogContent data-testid="purchase-dialog">
          <DialogHeader><DialogTitle>Log an on-sale purchase</DialogTitle></DialogHeader>
          <p className="-mt-1 text-xs text-muted-foreground">
            Records extra profit from a discounted buy. Your product's baseline cost and retail price stay the same.
          </p>
          {buy && (
            <div className="space-y-3">
              <div className="rounded-lg border p-2.5 text-sm">
                <div className="font-medium">{buy.name}{buy.size ? <span className="ml-1 text-xs text-muted-foreground">· {buy.size}</span> : null}</div>
                <div className="text-[11px] text-muted-foreground">Regular case price ${buy.case_price?.toFixed(2)}{buy.case_pack ? ` · ${buy.case_pack} per case` : ""}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Cases bought</Label><Input data-testid="purchase-cases-input" type="number" min="1" value={buyForm.cases} onChange={(e) => setBuyForm({ ...buyForm, cases: e.target.value })} /></div>
                <div><Label>Price paid / case ($)</Label><Input data-testid="purchase-paid-input" type="number" step="0.01" value={buyForm.paid} onChange={(e) => setBuyForm({ ...buyForm, paid: e.target.value })} placeholder={buy.case_price?.toFixed(2)} /></div>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-primary/5 px-3 py-2.5 text-sm" data-testid="purchase-savings-preview">
                <span className="text-muted-foreground">Extra profit from this deal</span>
                <span className={`font-mono font-bold ${buySavings() > 0 ? "text-green-700" : buySavings() < 0 ? "text-destructive" : ""}`}>${buySavings().toFixed(2)}</span>
              </div>
              {buy.case_pack && Number(buyForm.cases) > 0 ? (
                <p className="text-[11px] text-muted-foreground">{Number(buyForm.cases) * buy.case_pack} units at the discounted cost.</p>
              ) : null}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuy(null)} data-testid="purchase-cancel">Cancel</Button>
            <Button onClick={submitBuy} data-testid="purchase-save-button"><DollarSign className="mr-1 h-4 w-4" />Log purchase</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
